import "./ShippingOrderPending.scss"
import IconPencil from "assets/img/icon-pencil.svg?react"
import IconTrashBin from "assets/img/icon-trash-bin.svg?react"

import { FC, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Dayjs } from "utils/dayjs"
import cx from "classnames"

import ShippingOrder, { ShippingOrderStatus } from "models/ShippingOrder"
import Spoiler from "components/Spoiler/Spoiler"
import Button from "components/Button/Button"
import { Flex, FlexItem } from "components/Flex"
import NoValue from "components/NoValue/NoValue"
import Text from "components/Text/Text"
import DeliveryTerm from "models/DeliveryTerm"
import ShippingOrderEditModal from "components/ShippingOrderModal/ShippingOrderEditModal"
import Reservation from "models/Reservation"
import ShippingOrderArticles from "components/ShippingOrder/ShippingOrderArticles"
import { Grid } from "components/Grid"
import ConfirmModal from "components/ConfirmModal/ConfirmModal"
import toastMessages from "stores/toastMessages"
import AssetService, { ShippingOrderDeleteErrorPayload } from "services/AssetService"
import { EditSessionConfirmModalDuration, EditSessionDuration, ErrorCode, UserPermission } from "config/constants"
import CustomError from "models/CustomError"
import AlertModal from "components/AlertModal/AlertModal"
import { usePermissions } from "stores/permissions"
import { TenantWarehouse } from "models/TenantWarehouse"
import Summary from "components/Summary/Summary"
import ButtonLinkTooltip from "components/ButtonLinkTooltip/ButtonLinkTooltip"

interface IShippingOrderPendingProps {
    shippingOrder: ShippingOrder
    reservation: Reservation
    deliveryTerms?: DeliveryTerm[]
    tenantWarehouses?: TenantWarehouse[]
    onChanged(): void
}

const ShippingOrderPending: FC<IShippingOrderPendingProps> = ({
    shippingOrder,
    reservation,
    deliveryTerms,
    tenantWarehouses,
    onChanged,
}) => {
    const { t } = useTranslation()
    const { hasPermission } = usePermissions()
    const [expanded, setExpanded] = useState(false)
    const [showDeleteShippingOrderConfirmModal, setShowDeleteShippingOrderConfirmModal] = useState(false)
    const [showShippingOrderModal, setShowShippingOrderModal] = useState(false)
    const [isDeletingShippingOrder, setIsDeletingShippingOrder] = useState(false)

    const [deleteError, setDeleteError] = useState<{ code: ErrorCode, email: string, date: Dayjs | null } | null>(null)

    const canEdit = hasPermission(UserPermission.ShippingOrdersEdit) && shippingOrder.status === ShippingOrderStatus.pending
    const canDelete = !!shippingOrder.linkDelete

    const deliveryTerm = useMemo(() => {
        if (!shippingOrder.deliveryTermId) return
        return deliveryTerms?.find(dt => dt.termId === shippingOrder.deliveryTermId)
    }, [deliveryTerms, shippingOrder.deliveryTermId])

    const responsibleWarehouse = useMemo(() => {
        if (!shippingOrder.responsibleWarehouseId) return
        return tenantWarehouses?.find(w => w.id === shippingOrder.responsibleWarehouseId)
    }, [shippingOrder.responsibleWarehouseId, tenantWarehouses])

    const shippingOrderAssetIds = useMemo(() => {
        if (shippingOrder.assets.length === 0) return []
        return shippingOrder.assets.map((asset) => asset.assetId)
    }, [shippingOrder])

    async function handleDeleteShippingOrder(overrideOwnership?: boolean) {
        if (!canDelete || !shippingOrder.linkDelete) return

        resetDeleteError()
        setIsDeletingShippingOrder(true)

        try {
            await AssetService.deleteShippingOrder(shippingOrder.linkDelete, overrideOwnership)
            onChanged()
        } catch (e) {
            if (e instanceof CustomError &&
                (e.code === ErrorCode.forbidden || e.code === ErrorCode.conflict)) {
                const error: CustomError<ShippingOrderDeleteErrorPayload> = e
                setDeleteError({
                    code: e.code,
                    email: error.payload?.email ?? "",
                    date: error.payload?.date ?? null
                })
            } else {
                toastMessages.showSystemError()
            }
            setIsDeletingShippingOrder(false)
        }
    }

    function resetDeleteError() {
        setDeleteError(null)
    }

    function renderHeader(toggle?: () => void) {
        return (
            <Flex className="shipping-order-pending__header">
                <button type="button"
                    className="shipping-order-pending__title"
                    data-testid="shipping-order-pending-toggle-btn"
                    onClick={toggle}>
                    <Flex alignItems="baseline"
                        justifyContent="space-between">
                        <Text type="h5" className="mb-1">{ShippingOrder.getTypeLabel(shippingOrder.type)} ({shippingOrder.number})</Text>
                        {shippingOrder.status === ShippingOrderStatus.forwardedToShipping &&
                            <div className="font-italic color-grey-500">{t('shippingOrders.forwardedToShipping')}</div>
                        }
                    </Flex>
                    <Text className="color-grey-500">
                        {!!(shippingOrder.departureDate || shippingOrder.arrivalDate || shippingOrder.departureDateMin || shippingOrder.arrivalDateMax) &&
                            <>
                                {(shippingOrder.departureDateMin || shippingOrder.departureDate)?.format(t('config.dateLongTimeFormat')) || <NoValue />} - {(shippingOrder.arrivalDateMax || shippingOrder.arrivalDate)?.format(t('config.dateLongTimeFormat')) || <NoValue />}
                            </>
                        }
                        {!!shippingOrder.arrival?.address &&
                            <div className="white-space-pre-line">{shippingOrder.arrival.address.printZipCityCountry()}</div>
                        }
                    </Text>
                </button>
                {(canEdit || canDelete) &&
                    <Grid columns="repeat(2, max-content)"
                        gap="1.6rem"
                        className="shipping-order-pending__buttons">
                        {!!canDelete &&
                            <Button
                                round
                                theme="grey"
                                icon={<IconTrashBin />}
                                aria-label={t('common.delete')}
                                onClick={() => setShowDeleteShippingOrderConfirmModal(true)} />
                        }
                        {canEdit &&
                            <Button
                                round
                                theme="grey"
                                icon={<IconPencil />}
                                aria-label={t('common.edit')}
                                onClick={() => setShowShippingOrderModal(true)} />
                        }
                    </Grid>
                }
            </Flex>
        )
    }


    const departureDateDiffers = !!shippingOrder.departureDate && !!shippingOrder.departureDateMin && !shippingOrder.departureDate.isSame(shippingOrder.departureDateMin)
    const arrivalDateDiffers = !!shippingOrder.arrivalDate && !!shippingOrder.arrivalDateMax && !shippingOrder.arrivalDate.isSame(shippingOrder.arrivalDateMax)

    return (
        <>
            <div className={cx([
                "shipping-order-pending",
                {
                    "shipping-order-pending--expanded": expanded,
                    "shipping-order-pending--with-controls": canEdit || canDelete
                }
            ])}>
                <Spoiler
                    id={shippingOrder.id}
                    expanded={expanded}
                    onToggle={setExpanded}
                    toggleEl={(_, toggle) => renderHeader(toggle)}>
                    <div className="shipping-order-pending__content">
                        <Summary
                            data={[
                                {
                                    items: [
                                        {
                                            label: t('shippingOrders.loading'),
                                            value: !!shippingOrder.departure || !!shippingOrder.departureDate
                                                ? <div className="font-regular">
                                                    {!!shippingOrder.departure &&
                                                        <>
                                                            <strong>{shippingOrder.departure.description}</strong><br />
                                                            {shippingOrder.departure.address.print()}
                                                        </>
                                                    }
                                                    {!!(shippingOrder.departureDateMin || shippingOrder.departureDate) &&
                                                        <div className="font-italic">
                                                            {(shippingOrder.departureDateMin || shippingOrder.departureDate)?.format(t('config.dateLongTimeFormat'))}
                                                        </div>
                                                    }
                                                    {departureDateDiffers &&
                                                        <Text type="pSmallest" className="color-grey-500">
                                                            {t('shippingOrders.initialDate')}: {shippingOrder.departureDate?.format(t('config.dateLongTimeFormat'))}
                                                        </Text>
                                                    }
                                                </div>
                                                : <NoValue />
                                        },
                                        {
                                            label: t('shippingOrders.unloading'),
                                            value: !!shippingOrder.arrival || !!shippingOrder.arrivalDate
                                                ? <div className="font-regular">
                                                    {!!shippingOrder.arrival &&
                                                        <>
                                                            <strong>{shippingOrder.arrival.description}</strong><br />
                                                            {shippingOrder.arrival.address.print()}
                                                        </>
                                                    }
                                                    {!!(shippingOrder.arrivalDateMax || shippingOrder.arrivalDate) &&
                                                        <div className="font-italic">
                                                            {(shippingOrder.arrivalDateMax || shippingOrder.arrivalDate)?.format(t('config.dateLongTimeFormat'))}
                                                        </div>
                                                    }
                                                    {arrivalDateDiffers &&
                                                        <Text type="pSmallest" className="color-grey-500">
                                                            {t('shippingOrders.initialDate')}: {shippingOrder.arrivalDate?.format(t('config.dateLongTimeFormat'))}
                                                        </Text>
                                                    }
                                                </div>
                                                : <NoValue />
                                        },
                                        {
                                            label: t('shippingOrders.shippingInstructions'),
                                            value: shippingOrder.note || <NoValue />
                                        },
                                        {
                                            label: t('shippingOrders.termsOfDelivery'),
                                            value: deliveryTerms ? (deliveryTerm?.print() || <NoValue />) : <NoValue>{t('common.loading')}</NoValue>
                                        },
                                        {
                                            label: t('shippingOrders.responsibleWarehouse'),
                                            value: tenantWarehouses ? (responsibleWarehouse?.description || <NoValue />) : <NoValue>{t('common.loading')}</NoValue>
                                        },
                                        {
                                            label: t('reservation.contactPersonAtDeliveryAddress'),
                                            value: shippingOrder.personInCharge || <NoValue />
                                        },
                                        {
                                            label: t('reservation.contactPersonAtDeliveryAddressPhone'),
                                            value: shippingOrder.personInChargePhone || <NoValue />
                                        },
                                        {
                                            label: t('shippingOrders.forwardedToShipping'),
                                            value: shippingOrder.status === ShippingOrderStatus.pending ? t('common.no') : t('common.yes')
                                        }
                                    ],
                                    subSections: [{
                                        title: t('reservation.deliveryNoteText'),
                                        items: [
                                            {
                                                label: t('reservation.headerSection'),
                                                value: shippingOrder.deliveryNoteHeaderText
                                                    ? <div className="font-regular">
                                                        <ButtonLinkTooltip
                                                            label={t('reservation.showText')}
                                                            tooltipProps={{
                                                                whiteSpace: "pre-wrap",
                                                                content: shippingOrder.deliveryNoteHeaderText,
                                                                maxWidth: "800px",
                                                            }}
                                                        />
                                                    </div>
                                                    : <NoValue>{t('reservation.noText')}</NoValue>,
                                            },
                                            {
                                                label: t('reservation.footerSection'),
                                                value: shippingOrder.deliveryNoteFooterText
                                                    ? <div className="font-regular">
                                                        <ButtonLinkTooltip
                                                            label={t('reservation.showText')}
                                                            tooltipProps={{
                                                                whiteSpace: "pre-wrap",
                                                                content: shippingOrder.deliveryNoteFooterText,
                                                                maxWidth: "800px",
                                                            }}
                                                        />
                                                    </div>
                                                    : <NoValue>{t('reservation.noText')}</NoValue>,
                                            },
                                            {
                                                label: t('shippingOrders.printFeaturesOnDeliveryNote'),
                                                value: shippingOrder.deliveryNotePrintFeatures ? t('common.yes') : t('common.no'),
                                            }
                                        ],
                                    }]
                                }
                            ]}
                        />
                        <div className="mt-3">
                            <Flex alignItems="center" justifyContent="space-between" className="mb-3">
                                <FlexItem>
                                    <Text type="h5" className="mr-1">{t('shippingOrders.assetsToDeliver')}</Text>
                                </FlexItem>
                                <FlexItem>{shippingOrderAssetIds.length > 0 && t('common.xAssets', { count: shippingOrderAssetIds.length })}</FlexItem>
                            </Flex>
                            {shippingOrderAssetIds.length > 0 && !!reservation.articles &&
                                <ShippingOrderArticles
                                    shippingOrderAssetIds={shippingOrderAssetIds}
                                    nodeArticleId={reservation.articleId}
                                    reservationArticles={reservation.articles}
                                    purpose={reservation.purpose}
                                />
                            }
                            {shippingOrderAssetIds.length === 0 &&
                                <div className="pr-4 py-4 text-center">
                                    <div className="mb-4 white-space-pre-line">
                                        {t('shippingOrders.noAssetsToDeliver')}
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                </Spoiler>
            </div>
            {showShippingOrderModal &&
                <ShippingOrderEditModal
                    reservation={reservation}
                    shippingOrder={shippingOrder}
                    onClose={() => setShowShippingOrderModal(false)}
                    onSaved={onChanged}
                />
            }
            {showDeleteShippingOrderConfirmModal && canDelete &&
                <ConfirmModal
                    title={t('common.warning')}
                    confirmButtonLabel={t('common.delete')}
                    theme="danger"
                    maxWidth="586px"
                    onConfirm={() => handleDeleteShippingOrder()}
                    onClose={() => setShowDeleteShippingOrderConfirmModal(false)}
                    isBusy={isDeletingShippingOrder}>
                    {t("shippingOrders.deleteShippingOrderConfirm")}
                </ConfirmModal>
            }
            {deleteError?.code === ErrorCode.forbidden &&
                <ConfirmModal
                    theme="danger"
                    title={t('common.warning')}
                    maxWidth="640px"
                    onClose={resetDeleteError}
                    onConfirm={() => handleDeleteShippingOrder(true)}
                    confirmButtonLabel={t('common.confirm')}>
                    <div className="white-space-pre-line">{t('shippingOrders.updatedByDifferentUserX', { email: deleteError?.email ?? "", date: deleteError?.date?.format(t('config.dateLongTimeFormat')) ?? "" })}</div>
                </ConfirmModal>
            }
            {deleteError?.code === ErrorCode.conflict &&
                <AlertModal
                    title={t('common.attention')}
                    maxWidth="640px"
                    onClose={resetDeleteError}
                    buttonLabel={t('common.ok')}>
                    <div className="white-space-pre-line">{t('shippingOrders.lockedByDifferentUserX', { email: deleteError?.email ?? "", duration: EditSessionDuration.asMinutes() + EditSessionConfirmModalDuration.asMinutes() })}</div>
                </AlertModal>
            }
        </>
    )
}

export default ShippingOrderPending
